// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { DisclosedContract as TokenStandardApiDisclosedContract } from '@digitalasset/allocation-instruction-api-ts-client';
import { JsActiveContract } from '@digitalasset/json-api-ts-client/index';
import { DisclosedContract as ApiDisclosedContract } from '@digitalasset/utility-operator-api-ts-client';
import { GridFilterItem } from '@mui/x-data-grid';
import { Set } from '@daml.js/daml-set-types/lib/DA/Set/Types';
import { InstrumentIdentifier } from '@daml.js/utility-registry-holding-v0/lib/Utility/Registry/Holding/V0/Types';
import { ContractId, DisclosedContract, emptyMap } from '@daml/types';

export const fmt = (x: number | string, decimals?: number) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: decimals,
    maximumFractionDigits: 20,
  });
  const res = formatter.format(typeof x === 'string' ? parseFloat(x) : x);
  return res;
};

export const emptySet = <T>(): Set<T> => ({ map: emptyMap<T, object>() });
export const prettyParty = (party: string): string => {
  const [p] = party.split('::');
  return p;
};
export const instrumentEquals = (k1: InstrumentIdentifier, k2: InstrumentIdentifier): boolean => {
  return k1.source === k2.source && k1.id === k2.id && k1.scheme === k2.scheme;
};

export const createSet = <T>(vals: T[]): Set<T> => {
  let map = emptyMap<T, object>();
  for (let i = 0; i < vals.length; i += 1) map = map.set(vals[i], {});
  return { map };
};

export function values<T>(set: Set<T>): T[] {
  try {
    return set.map.entriesArray().map((v) => v[0]);
  } catch {
    console.error(`Could not get values from set: ${JSON.stringify(set)}`);
    return [];
  }
}

export const shorten = (s: string, len?: number): string => {
  return s.substring(0, len || 8);
};

export const extractPrefix = (input: string, delimiter: string = '::'): string => {
  const [prefix] = input.split(delimiter);
  return prefix;
};

export const filterToSqlText = (f: GridFilterItem): string => {
  if (typeof f.value === 'string') {
    const s = f.value.replaceAll('%', '');
    if (f.operator === 'startsWith') {
      return `${s}%`;
    }
    if (f.operator === 'equals') {
      return s;
    }
  }
  throw new Error('Invalid filter');
};

export const filterDateToSqlText = (f: GridFilterItem): string => {
  const date = new Date(f.value as string);
  if (f.operator === 'before') {
    return `< '${date.toISOString()}'`;
  }
  if (f.operator === 'after') {
    return `> '${date.toISOString()}'`;
  }
  throw new Error('Invalid filter');
};

export const instrumentIdentifierEquals = (
  k1: InstrumentIdentifier,
  k2: InstrumentIdentifier,
): boolean => {
  return k1.source === k2.source && k1.id === k2.id && k1.scheme === k2.scheme;
};

export const parseDateString = (dateAsString: string): Date => {
  const parsedFromString = Date.parse(dateAsString);
  return new Date(parsedFromString);
};

export const toDisclosedContract = (activeContract: JsActiveContract): DisclosedContract => ({
  contractId: activeContract.createdEvent.contractId as ContractId<unknown>,
  templateId: activeContract.createdEvent.templateId,
  createdEventBlob: activeContract.createdEvent.createdEventBlob,
});

export const toDamlDisclosedContract = (
  disclosedContract: ApiDisclosedContract,
): DisclosedContract => ({
  contractId: disclosedContract.contractId as ContractId<unknown>,
  templateId: disclosedContract.templateId,
  createdEventBlob: disclosedContract.createdEventBlob,
});

export const toTokenStandardDisclosedContract = (
  disclosedContract: TokenStandardApiDisclosedContract,
): DisclosedContract => ({
  contractId: disclosedContract.contractId as ContractId<unknown>,
  templateId: disclosedContract.templateId,
  createdEventBlob: disclosedContract.createdEventBlob,
});
